import {computed, computedFn, _} from '@api'
import {FLEX_WEIGHT_DEFAULT, globalStores} from '@stores'
import {FLEX_INLINE_TAB_HEIGHT} from '@stores/catalog/flexLayout'
import {action, observable} from 'mobx'
import {BorderNode, DefaultNodeType, FlexLayoutNode, IDraggable, IDropTarget, ILayoutMetrics, Model, RowNode, TabNode} from '.'
import {Attribute, AttributeDefinitions, DockLocation, DropInfo, IJsonTabNode, IJsonTabSetNode, Orientation, Rect, TabSetStore} from '../'
import {adjustSelectedIndex} from './Utils'

export class TabSetNode extends FlexLayoutNode<DefaultNodeType & { config: any, tabStripHeight?: number, enableTabStrip: boolean, enableDivide: boolean, enableMaximize: boolean, enableDrag: boolean, enableDeleteWhenEmpty: boolean }, IJsonTabSetNode> implements IDraggable, IDropTarget {
  static readonly TYPE = 'tabset'
  @observable.ref component: TabSetStore
  @computed get isInModel() { return this.model._idMap[this.id] != null}

  get tabs() { return this.children as Array<TabNode>}
  @action closeAllTabsInGroup = (removeFromParent = true) => {
    this._children = []

    if (removeFromParent) {
      const {parent, parent: {_children}} = this
      parent._children = _children.filter(n => n != this)
      this.model.onChange()
    }
  }

  @computed get showCollapsed() {
    return globalStores.flexLayout.hoveredTabSet === this.id
  }

  @computed get isInColumn() {
    return this.parent.isVertical
  }

  /**
   * If we're inside of a row node, the next tabset (the one to our right)
   */
  @computed get neighborRight(): TabSetNode | RowNode {
    let {parent, indexInParent} = this
    if (parent instanceof RowNode) {
      if (parent.isVertical) {
        parent = parent.parent
        indexInParent = this.parent.indexInParent
      }

      const {children} = parent

      if (indexInParent + 1 === children.length) return null
      else {
        return children[indexInParent + 1] as TabSetNode | RowNode
      }
    }

    return null
  }

  @computed get neighborDown(): TabSetNode | RowNode {
    const {parent} = this
    if (parent instanceof RowNode && parent.orientation.name === 'vert') {
      const {children} = parent, {indexInParent} = this

      if (indexInParent + 1 === children.length) return null
      else {
        return children[indexInParent + 1] as TabSetNode | RowNode
      }
    }

    return null
  }

  @action addChildJson = (tabJson: IJsonTabNode, select: boolean = true, index?: number/*, updateModelViaJson?: boolean*/) => {
    // if (updateModelViaJson) { // Slow and causes reload
    //   const {json} = this
    //   json.children.splice(_.isNumber(index) ? index : json.children.length, 0, tabJson)
    //   if (select) {
    //     json.selected = _.isNumber(index) ? index : json.children.length - 1
    //   }
    // } else {
      const {_children, model} = this,
        node = TabNode._fromJson(tabJson, model)

      node.parent = this
      _children.splice(_.isNumber(index) ? index : _children.length, 0, node)
      if (select || !this.selectedNode) {
        this._setSelected(_.isNumber(index) ? index : _children.length - 1)
      }
      return node
    // }
  }

  /** @hidden @internal */
  static _fromJson(json: IJsonTabSetNode, model: Model) {
    const newLayoutNode = new TabSetNode(model, json)
    json.children = json.children ?? []
    json.children.forEach((jsonChild: any) => {
      const child = TabNode._fromJson(jsonChild, model)
      child.parent = newLayoutNode
      newLayoutNode.addChild(child)
    })

    json.maximized = false
    if (json.maximized && json.maximized === true) {
      model._setMaximizedTabset(newLayoutNode)
    }

    if (json.active && json.active === true) {
      model._setActiveTabset(newLayoutNode)
    }

    if (!_.isNumber(json.selected) || json.selected > json.children.length - 1) {
      json.selected = 0
    }

    return newLayoutNode
  }

  /** @hidden @internal */
  private static _attributeDefinitions: AttributeDefinitions = TabSetNode._createAttributeDefinitions()

  /** @hidden @internal */
  private static _createAttributeDefinitions(): AttributeDefinitions {
    const attributeDefinitions = new AttributeDefinitions()
    attributeDefinitions.add('type', TabSetNode.TYPE, true).setType(Attribute.STRING).setFixed()
    attributeDefinitions.add('id', undefined).setType(Attribute.STRING)

    attributeDefinitions.add('weight', 100).setType(Attribute.NUMBER)
    attributeDefinitions.add('width', undefined).setType(Attribute.NUMBER)
    attributeDefinitions.add('height', undefined).setType(Attribute.NUMBER)
    attributeDefinitions.add('selected', 0).setType(Attribute.NUMBER)
    attributeDefinitions.add('name', undefined).setType(Attribute.STRING)
    attributeDefinitions.add('config', undefined).setType('any')

    attributeDefinitions.addInherited('enableDeleteWhenEmpty', 'tabSetEnableDeleteWhenEmpty')
    attributeDefinitions.addInherited('enableDrop', 'tabSetEnableDrop')
    attributeDefinitions.addInherited('enableDrag', 'tabSetEnableDrag')
    attributeDefinitions.addInherited('enableDivide', 'tabSetEnableDivide')
    attributeDefinitions.addInherited('enableMaximize', 'tabSetEnableMaximize')
    attributeDefinitions.addInherited('classNameTabStrip', 'tabSetClassNameTabStrip')
    attributeDefinitions.addInherited('classNameHeader', 'tabSetClassNameHeader')
    attributeDefinitions.addInherited('enableTabStrip', 'tabSetEnableTabStrip')
    attributeDefinitions.addInherited('borderInsets', 'tabSetBorderInsets')
    attributeDefinitions.addInherited('marginInsets', 'tabSetMarginInsets')
    attributeDefinitions.addInherited('minWidth', 'tabSetMinWidth')
    attributeDefinitions.addInherited('minHeight', 'tabSetMinHeight')

    attributeDefinitions.addInherited('headerHeight', 'tabSetHeaderHeight')
    attributeDefinitions.addInherited('tabStripHeight', 'tabSetTabStripHeight')
    attributeDefinitions.addInherited('tabLocation', 'tabSetTabLocation')
    attributeDefinitions.addInherited('autoSelectTab', 'tabSetAutoSelectTab').setType(Attribute.BOOLEAN)
    return attributeDefinitions
  }

  /**
   * Filter out tabs which are invalid due to the selected customer deployment
   *
   * Don't delete them as we want them to come back when we switch back to the other deploy
   */
  override get children() {
    const {_children, flexLayout: {stores: {deployments: {selectedDeploymentId}}}} = this
    return _children
    // return _children.filter((tab: TabNode) => tab.component != 'catalog' || !selectedDeploymentId )
  }

  /** @hidden @internal */
  @observable.ref private _contentRect?: Rect
  /** @hidden @internal */
  @observable.ref private _tabHeaderRect?: Rect
  /** @hidden @internal */
  @observable calculatedHeaderBarHeight: number

  /** @hidden @internal */
  constructor(model: Model, json: any) {
    super(model)

    TabSetNode._attributeDefinitions.fromJson(json, this._attributes)
    model._addNode(this)
    this.calculatedHeaderBarHeight = 0
  }

  get name() {
    return this._getAttr('name') as string | undefined
  }

  @computed get selected() {
    const {_attributes: {selected}, children} = this

    let r = selected ?? 0

    return r >= children.length ? 0 : r
  }

  @computed get selectedNode() : TabNode {
    const selected = this.selected
    if (selected !== -1) {
      return this._children[selected] as TabNode
    }
    return undefined
  }

  get weight(): number {
    return this._getAttr('weight') as number
  }

  get width() {
    return this._getAttr('width') as number | undefined
  }

  get minWidth() {
    return this._getAttr('minWidth') as number
  }

  get height() {
    return this._getAttr('height') as number | undefined
  }

  get minHeight() {
    return this._getAttr('minHeight') as number
  }

  /** @hidden @internal */
  getMinSize(orientation: Orientation) {
    if (orientation === Orientation.HORZ) {
      return this.minWidth
    } else {
      return this.minHeight
    }
  }

  /**
   * Returns the config attribute that can be used to store node specific data that
   * WILL be saved to the json. The config attribute should be changed via the action Actions.updateNodeAttributes rather
   * than directly, for example:
   * this.state.model.doAction(
   *   FlexLayout.Actions.updateNodeAttributes(node.id, {config:myConfigObject}));
   */
  get config() {
    return this._attributes.config
  }

  @computed get isMaximized() {
    return this.flexLayout.maximizedTabSet?.id == this.id
  }

  @computed get isMaximized_OldFlexLayout() {
    return this._model.maximizedTabset === this
  }

  @computed get isActive() {
    return this._model?.activeTabset === this
  }

  set isActive(v) {
    if (v) {
      this._model._setActiveTabset(this)
    }
  }

  @computed get isEnableDeleteWhenEmpty() {
    return this._getAttr('enableDeleteWhenEmpty') as boolean
  }

  @computed get isEnableDrop() {
    return this._getAttr('enableDrop') as boolean
  }

  @computed get isEnableDrag() {
    return this._getAttr('enableDrag') as boolean
  }

  @computed get isEnableDivide() {
    return this._getAttr('enableDivide') as boolean
  }

  @computed get isEnableMaximize() {
    return this._getAttr('enableMaximize') as boolean
  }

  @computed get canMaximize() {
    if (this.isEnableMaximize && !this.isLayoutSidebar) {
      // always allow maximize toggle if already maximized
      if (this.model.maximizedTabset === this) {
        return true
      }
      // only one tabset, so disable
      // if (this.parent === this.model.root && this.model.root.children.length === 1) {
      //     return false;
      // }
      return true
    }
    return false
  }

  @computed get enableTabStrip() {
    return this._getAttr('enableTabStrip') as boolean
  }

  set enableTabStrip(v: boolean) {
    this.attributes.enableTabStrip = v
    this.model.layout.store.asyncDoLayout()
  }

  @computed get isAutoSelectTab() {
    return this._getAttr('autoSelectTab') as boolean
  }

  @computed get classNameTabStrip() {
    return this._getAttr('classNameTabStrip') as string | undefined
  }

  @computed get classNameHeader() {
    return this._getAttr('classNameHeader') as string | undefined
  }

  /** @hidden @internal */
  @action calculateHeaderBarHeight = (metrics: ILayoutMetrics) => {
    const headerBarHeight = this._getAttr('headerHeight') as number
    if (headerBarHeight !== 0) {
      // its defined
      this.calculatedHeaderBarHeight = headerBarHeight
    } else {
      this.calculatedHeaderBarHeight = metrics.headerBarSize
    }
  }

  @computed get headerHeight() {
    return this.calculatedHeaderBarHeight
  }

  @computed get tabStripHeight() {
    const {_children, _attributes, model, flexLayout: {hideTabSets}} = this
    if (hideTabSets) return 0

    if (false && _children.some((tab: TabNode) => tab.component === 'inlineCatalogItemDetails')) {
      return FLEX_INLINE_TAB_HEIGHT
    } else {
      const tabBarHeight = this._getAttr('tabStripHeight') as number
      if (tabBarHeight !== 0) {
        // its defined
        return tabBarHeight
      } else {
        return model._getAttribute('borderBarSize')
      }
    }
  }

  get tabLocation() {
    return this._getAttr('tabLocation') as string
  }

  /** @hidden @internal */
  _setSelected = (index: number) => {
    this._attributes.selected = index
    this.model.onChange()
  }

  /** @hidden @internal */
  canDrop = (dragNode: FlexLayoutNode & IDraggable, x: number, y: number): DropInfo | undefined => {
    let dropInfo

    if (dragNode === this) {
      const dockLocation = DockLocation.CENTER
      const outlineRect = this._tabHeaderRect
      dropInfo = new DropInfo(this, outlineRect!, dockLocation, -1, 'flexlayout__outline_rect')
    } else if (this._contentRect!.contains(x, y)) {
      const dockLocation = DockLocation.getLocation(this._contentRect!, x, y)
      const outlineRect = dockLocation.getDockRect(this.rect)
      dropInfo = new DropInfo(this, outlineRect, dockLocation, -1, 'flexlayout__outline_rect')
    } else if (this._children.length > 0 && this._tabHeaderRect != null && this._tabHeaderRect.contains(x, y)) {
      let child = this._children[0] as TabNode
      let r = child.tabRect!
      const yy = r.y
      const h = r.height
      let p = this._tabHeaderRect.x
      let childCenter = 0
      for (let i = 0; i < this._children.length; i++) {
        child = this._children[i] as TabNode
        r = child.tabRect!
        childCenter = r.x + r.width / 2
        if (_.isNaN(childCenter)) debugger
        if (x >= p && x < childCenter) {
          const dockLocation = DockLocation.CENTER
          const outlineRect = new Rect(r.x - 2, yy, 3, h)
          dropInfo = new DropInfo(this, outlineRect, dockLocation, i, 'flexlayout__outline_rect')
          break
        }
        p = childCenter
      }
      if (dropInfo == null) {
        const dockLocation = DockLocation.CENTER
        const outlineRect = new Rect(r.right - 2, yy, 3, h)
        dropInfo = new DropInfo(this, outlineRect, dockLocation, this._children.length, 'flexlayout__outline_rect')
      }
    }

    if (!dragNode._canDockInto(dragNode, dropInfo)) {
      return undefined
    }

    return dropInfo
  }

  /** @hidden @internal */
  @action _layout = (rect: Rect, metrics: ILayoutMetrics) => {
    this.calculateHeaderBarHeight(metrics)

    if (this.isMaximized_OldFlexLayout) {
      rect = (this._model.root as FlexLayoutNode).rect
    }

    rect = rect.removeInsets(this._getAttr('marginInsets'))
    if (!rect.equals(this._rect)) {
      this._rect = rect
    }

    rect = rect.removeInsets(this._getAttr('borderInsets'))

    const showHeader = false && this.name !== undefined
    let y = 0
    let h = 0
    if (showHeader) {
      y += this.calculatedHeaderBarHeight
      h += this.calculatedHeaderBarHeight
    }
    if (this.enableTabStrip) {
      const tabHeaderRect = this.tabLocation === 'top' ? new Rect(rect.x, rect.y + y, rect.width, this.tabStripHeight) : new Rect(rect.x, rect.y + rect.height - this.tabStripHeight, rect.width, this.tabStripHeight)
      if (!tabHeaderRect.equals(this._tabHeaderRect)) {
        this._tabHeaderRect = tabHeaderRect
      }

      h += this.tabStripHeight
      if (this.tabLocation === 'top') {
        y += this.tabStripHeight
      }
    }
    const contentRect = new Rect(rect.x, rect.y + y, rect.width, rect.height - h)
    if (!contentRect.equals(this._contentRect)) {
      this._contentRect = contentRect
    }

    this._children.forEach((child, i) => {
      child._layout(this._contentRect!, metrics)
    })
  }

  /** @hidden @internal */
  @action _remove = (node: TabNode) => {
    const removedIndex = this._removeChild(node)
    this._model.tidy()

    adjustSelectedIndex(this, removedIndex)
  }

  /** @hidden @internal */
  @action drop = (dragNode: FlexLayoutNode & IDraggable, location: DockLocation, index: number, select?: boolean) => {
    const dockLocation = location

    if (this === dragNode) {
      // tabset drop into itself
      return // dock back to itself
    }

    let dragParent = dragNode.parent as BorderNode | TabSetNode | RowNode
    let fromIndex = 0
    if (dragParent !== undefined) {
      fromIndex = dragParent._removeChild(dragNode)
      adjustSelectedIndex(dragParent, fromIndex)
    }

    // if dropping a tab back to same tabset and moving to forward position then reduce insertion index
    if (dragNode.type === TabNode.TYPE && dragParent === this && fromIndex < index && index > 0) {
      index--
    }

    // simple_bundled dock to existing tabset
    if (dockLocation === DockLocation.CENTER) {
      let insertPos = index
      if (insertPos === -1) {
        insertPos = this._children.length
      }

      if (dragNode.type === TabNode.TYPE) {
        this.addChild(dragNode, insertPos)
        if (select || (select !== false && this.isAutoSelectTab)) {
          this._setSelected(insertPos)
        }
        // console.log("added child at : " + insertPos);
      } else {
        dragNode.children.forEach((child, i) => {
          this.addChild(child, insertPos)
          // console.log("added child at : " + insertPos);
          insertPos++
        })
      }
      this._model._setActiveTabset(this)
    } else {
      let tabSet: TabSetNode | undefined
      if (dragNode instanceof TabNode) {
        // create new tabset parent
        // console.log("create a new tabset");
        const callback = this._model._getOnCreateTabSet()
        tabSet = new TabSetNode(this._model, callback ? callback(dragNode as TabNode) : {})
        tabSet.addChild(dragNode)
        // console.log("added child at end");
        dragParent = tabSet
      } else {
        tabSet = dragNode as TabSetNode
      }

      const parentRow = this.parent as FlexLayoutNode
      const pos = parentRow.children.indexOf(this)

      if (parentRow.orientation === dockLocation._orientation) {
        tabSet._setWeight(this.weight / 2)
        this._setWeight(this.weight / 2)
        // console.log("added child 50% size at: " +  pos + dockLocation.indexPlus);
        parentRow.addChild(tabSet, pos + dockLocation._indexPlus)
      } else {
        // create a new row to host the new tabset (it will go in the opposite direction)
        // console.log("create a new row");
        const newRow = new RowNode(this._model, {})
        newRow._setWeight(this.weight)
        newRow.addChild(this)
        this._setWeight(50)
        tabSet._setWeight(50)
        // console.log("added child 50% size at: " +  dockLocation.indexPlus);
        newRow.addChild(tabSet, dockLocation._indexPlus)

        parentRow._removeChild(this)
        parentRow.addChild(newRow, pos)
      }
      this._model._setActiveTabset(tabSet)
    }
    this._model.tidy()
  }

  /** @hidden @internal */
  @computed get asJson(): any {
    const json: any = {}
    TabSetNode._attributeDefinitions.toJson(json, this._attributes)
    json.children = this._children.map((child) => child.asJson)

    if (this.isActive) {
      json.active = true
    }

    if (this.isMaximized_OldFlexLayout) {
      json.maximized = true
    }

    return json
  }

  /** @hidden @internal */
  @action _updateAttrs = (json: any) => {
    TabSetNode._attributeDefinitions.update(json, this._attributes)
  }

  /** @hidden @internal */
  _getAttributeDefinitions() {
    return TabSetNode._attributeDefinitions
  }

  /** @hidden @internal */
  _getPrefSize = (orientation: Orientation) => {
    let prefSize = this.width
    if (orientation === Orientation.VERT) {
      prefSize = this.height
    }
    return prefSize
  }

  /** @hidden @internal */
  static getAttributeDefinitions() {
    return TabSetNode._attributeDefinitions
  }

  get row() { return this.parent as RowNode }
  get column() {
    const {row} = this
    return row.isVertical ? row : null
  }

  @computed get shorthand() {
    const {children} = this
    let r = ''
    children.forEach(t => {
      r += t.id.length === 1 ? t.id : '#'
    })

    return r
  }
  @action selectNextTab = () => {
    console.log(`Select next tab`)
    const {model, attributes, attributes: {selected: cur}, _children} = this
    attributes.selected = cur < _children.length - 1 ? cur + 1 : 0
    model.lastAction = 'FlexLayout_SelectTab'
  }

  @action selectPreviousTab = () => {
    console.log(`Select previous tab`)
    const {model, attributes, attributes: {selected: cur}, _children} = this
    attributes.selected = cur > 0 ? cur - 1 : _children.length - 1
    model.lastAction = 'FlexLayout_SelectTab'
  }

  @action addTab = (props?: Partial<IJsonTabNode>) => {
    return this.addChildJson({type: 'tab', ...(props ?? {})})
  }

  @computed get lastTab() : TabNode {
    return _.last(this.tabs)
  }

  @action uncollapse = () => {
    const {weight, _setWeight} = this
    if (weight === 0) {
      //const {parent} = this // Todo - find related splitter and call toggleSplitWeights()
      const maxSiblingWeight = _.max(this.parent.children.map(v => Number(v?.attributes?.weight)))
      _setWeight(maxSiblingWeight)
    }

    this.row && this.row.uncollapse()
  }

  @action removeAllButSelectedTab = () => {
    const {selectedNode, children} = this
    children.forEach(v => v != selectedNode && v.removeFromParent())
  }
}



// WEBPACK FOOTER //
// ./src/stores/catalog/flexLayout/flexlayout-react/src/model/TabSetNode.ts